import locationsCheck from '../helpers/locationsCheck';
import { validateAddons } from '../helpers/validateAddons';
import checkIfAddOnsAreComplete from '../helpers/checkIfAddonsAreComplete';
import { areAllEndorsementsValid } from '../Components/Endorsements/shared/endorsementValidation';
import checkIfEndorsementsAreComplete from '../Components/Endorsements/shared/checkIfEndorsementsAreComplete';
import {
  selectAdditionalInsureds,
  selectAnnualPayroll,
  selectApplication,
  selectLegalBusinessName,
  selectCertificationsMaintained,
  selectCertificationsRequired,
  selectContactFirstName,
  selectContactLastName,
  selectContactPhone,
  selectFailFastZip,
  selectGLLimit,
  selectGrossAnnualSales,
  selectAkHash,
  selectMailingAddressCity,
  selectMailingAddressState,
  selectMailingAddressStreet,
  selectMailingAddressZip,
  selectLocationStreet,
  selectLocationCity,
  selectLocationZip,
  selectLocationState,
  selectNumEmployees,
  selectPlDeductible,
  selectPlLimit,
  selectPolicyTypes,
  selectPreviousLosses,
  selectUserHasSelectedAdditionalInsureds,
  selectYearsOfPriorActs,
  selectYearsOfProfessionalExperience,
  selectEndorsements,
  selectContactEmailIsValid,
  selectHasSprinklers,
  selectHasLocationValidationError,
  selectLimitDamagePremRented,
  selectContractorToolsLimit,
  selectBusinessStartDate,
  selectBusinessAgeInMonths,
  selectIsRetroactiveDateValid,
  selectShowPlv2,
  selectIsEndorsementsRefactorEnabled,
  selectCheckedEndorsements,
} from './application';
import { getAvailableEndorsements } from './availableEndorsements';
import { validatePhoneNumber } from '../helpers/inputValidation/validatePhoneNumber';
import { createSelector } from 'reselect';
import { pageOrder, PAGES } from '../constants';
import { selectDBAIsValid } from './index';
import { selectIsStateZipVerified } from './businessInfo';
import { isBusinessStartDateValid } from '../helpers/isBusinessStartDateValid';
import { isYearsOfPriorActsValid } from '../helpers/isYearsOfPriorActsValid';

// Page Completed
export const isIndustryComplete = (state: ReduxState) => Boolean(selectAkHash(state));
export const isBusinessInfoComplete = (state: ReduxState) => {
  const businessStartDate = selectBusinessStartDate(state);
  const legalBusinessName = selectLegalBusinessName(state);
  const numEmployees = selectNumEmployees(state);

  return (
    numEmployees &&
    numEmployees > 0 &&
    Number.isInteger(numEmployees) &&
    (selectFailFastZip(state).length === 5 || selectFailFastZip(state).length === 10) &&
    Array.isArray(selectPreviousLosses(state)) &&
    legalBusinessName !== '' &&
    legalBusinessName &&
    Boolean(selectLocationState(state)) &&
    selectDBAIsValid(state) &&
    selectIsStateZipVerified(state) &&
    businessStartDate &&
    isBusinessStartDateValid(new Date(businessStartDate)).isValid
  );
};

export const isPoliciesComplete = (state: ReduxState) => Boolean(selectPolicyTypes(state).length);
export const isFinanceBasicsComplete = (state: ReduxState) =>
  Boolean(selectGrossAnnualSales(state) && selectAnnualPayroll(state));

export const isLocationsComplete = (state: ReduxState) => {
  const locations = locationsCheck(selectApplication(state)) || [];
  if (!locations.length) return false;

  const applicationTypes = selectPolicyTypes(state);
  const locationStreet = selectLocationStreet(state);
  const locationCity = selectLocationCity(state);
  const locationState = selectLocationState(state);
  const locationZip = selectLocationZip(state);
  const hasSprinklers = selectHasSprinklers(state);
  const areLocationComplete = [locationStreet, locationCity, locationState, String(locationZip)].every(
    (value) => value && value.length > 1
  );
  const areBizLocComplete = locations.reduce((result: boolean, location: Location) => {
    if (!result) return result;

    const { locationType, buildingLimit } = location;
    const typesWithoutBuildingLimit = ['CommercialCondoLeased', 'Home', 'BuildingLeased', 'Kiosk', 'Other'];
    const hasBuildingLimit = typesWithoutBuildingLimit.includes(locationType) || buildingLimit != null;

    return Boolean(locationType && hasBuildingLimit && typeof hasSprinklers === 'boolean');
  }, true);

  const hasValidationError = selectHasLocationValidationError(state);

  if (applicationTypes.includes('BOP')) {
    return areBizLocComplete && areLocationComplete && !hasValidationError;
  } else {
    return areLocationComplete;
  }
};

export const isGLComplete = (state: ReduxState) => Boolean(selectGLLimit(state));

interface isPLCompleteOptions {
  ignoreDefaults: boolean;
}

export const isPLComplete = (state: ReduxState, options: isPLCompleteOptions = { ignoreDefaults: false }) => {
  const occurrenceLimit = selectPlLimit(state);
  const deductibleAmount = selectPlDeductible(state);
  const yearsOfPriorActs = selectYearsOfPriorActs(state);
  const certificationsRequired = selectCertificationsRequired(state);
  const certificationsMaintained = selectCertificationsMaintained(state);
  const yearsOfProfessionalExperience = selectYearsOfProfessionalExperience(state);
  const businessStartDate = selectBusinessStartDate(state);
  const businessAgeInMonths = selectBusinessAgeInMonths(state);
  const isRetroactiveDateValid = selectIsRetroactiveDateValid(state);
  const isOccurrenceLimitComplete = typeof occurrenceLimit !== 'undefined' && occurrenceLimit >= 0;
  const isDeductibleAmountComplete = typeof deductibleAmount !== 'undefined' && deductibleAmount >= 0;
  const areDefaultsComplete = options.ignoreDefaults || (isOccurrenceLimitComplete && isDeductibleAmountComplete);
  const showPlv2 = selectShowPlv2(state);
  return (
    areDefaultsComplete &&
    typeof businessStartDate !== 'undefined' &&
    typeof businessAgeInMonths !== 'undefined' &&
    (showPlv2
      ? isRetroactiveDateValid.isValid
      : typeof yearsOfPriorActs !== 'undefined' && isYearsOfPriorActsValid(yearsOfPriorActs, businessAgeInMonths)) &&
    typeof certificationsRequired !== 'undefined' &&
    typeof certificationsMaintained !== 'undefined' &&
    typeof yearsOfProfessionalExperience !== 'undefined'
  );
};

export const isAdditionalInsuredsComplete = (state: ReduxState) => {
  const shouldShowAdditionalInsuredsForm = selectUserHasSelectedAdditionalInsureds(state);
  const additionalInsureds = selectAdditionalInsureds(state);

  return Boolean(
    (shouldShowAdditionalInsuredsForm && additionalInsureds.length) || shouldShowAdditionalInsuredsForm === false
  );
};

export const isContactInfoComplete = (state: ReduxState) => {
  const isContactEmailValid = selectContactEmailIsValid(state) !== false;
  const isContactPhoneValid = validatePhoneNumber(selectContactPhone(state));
  const contactFirstName = selectContactFirstName(state);
  const contactLastName = selectContactLastName(state);
  const mailingAddressStreet = selectMailingAddressStreet(state);
  const mailingAddressCity = selectMailingAddressCity(state);
  const mailingAddressState = selectMailingAddressState(state);
  const mailingAddressZip = selectMailingAddressZip(state);
  const mailingComplete = [mailingAddressStreet, mailingAddressCity, mailingAddressState, mailingAddressZip].every(
    (value) => value && value.length > 1
  );

  return Boolean(contactFirstName && contactLastName && isContactEmailValid && isContactPhoneValid && mailingComplete);
};

// ensures checked endorsements are complete and valid
export const isNewEndorsementsComplete = (state: ReduxState) => {
  const checkedEndorsements = selectCheckedEndorsements(state);
  //  checks if required fields are present (i.e., the user has filled in all necessary inputs). Example: EPLI needs both limits and retention to be non-null.
  const areComplete = checkIfEndorsementsAreComplete(checkedEndorsements, state);
  // checks if present values are within acceptable bounds. Example: EPLI limits must be one of '50000/50000', '100000/100000', '250000/250000'.
  const areValid = areAllEndorsementsValid(state);
  return areComplete && areValid;
};

export const isAddOnsComplete = (
  state: ReduxState,
  checkFn: typeof checkIfAddOnsAreComplete = checkIfAddOnsAreComplete
) => {
  if (selectIsEndorsementsRefactorEnabled(state)) {
    return isNewEndorsementsComplete(state);
  }
  const availableEndorsements = getAvailableEndorsements(state);
  const endorsementsState = selectEndorsements(state);
  const areAddOnsComplete = checkFn(availableEndorsements, endorsementsState);
  const areAddOnsValid = validateAddons(state);
  const limitDamagePremRented = selectLimitDamagePremRented(state);
  const contractorToolsLimit = selectContractorToolsLimit(state);

  // when a user opens the contractor tools add on, the default limit will be set to 5,000.  This needs to be overridden by selecting a sub option for the next button to be come enabled
  const hasContractorToolsLimitDefault = contractorToolsLimit && contractorToolsLimit === 5000;

  // the limitDamagePremRented add on comes with a base limit of 50k so anything added by the user is additional to this base limit
  // limitDamagePremRented is considered valid if the limit is greater than or equal to $50,000
  const isLimitDamagePremRentedValid =
    typeof limitDamagePremRented === 'number' ? limitDamagePremRented >= 50_000 : true;

  return areAddOnsComplete && areAddOnsValid && isLimitDamagePremRentedValid && !hasContractorToolsLimitDefault;
};

const shouldRenderPageLink = (applicationTypes: string[], availableEndorsements: AvailableEndorsementsState) => {
  const areEndorsementsAvailable = availableEndorsements != null;
  const noneSelected = applicationTypes.length === 0;
  const isBopSelected = applicationTypes.includes('BOP');
  const isGlSelected = applicationTypes.includes('GL');
  const isPlSelected = applicationTypes.includes('PL');
  const isBopOrGlSelected = isBopSelected || isGlSelected;
  const shouldRenderAddons = areEndorsementsAvailable;

  return pageOrder.filter((page: string) => {
    if (page === PAGES.LOCATIONS) {
      if (noneSelected) {
        return false;
      }
    } else if (page === PAGES.GENERAL_LIABILITY) {
      if (noneSelected || !isBopOrGlSelected) {
        return false;
      }
    } else if (page === PAGES.PROFESSIONAL_LIABILITY) {
      if (noneSelected || !isPlSelected) {
        return false;
      }
    } else if (page === PAGES.ADD_ONS) {
      if (!shouldRenderAddons) {
        return false;
      }
    }

    return true;
  });
};

export const selectAvailablePages = createSelector(selectPolicyTypes, getAvailableEndorsements, shouldRenderPageLink);
